import React, { useEffect, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import {
    AppBar,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    ThemeProvider,
    Theme,
    StyledEngineProvider,
    Toolbar,
    Typography,
} from "@mui/material";
import Hidden from "@mui/material/Hidden";
import Navigator from "../../components/layout/Navigator";
import { useBaseLayoutStyle } from "./BaseLayout.style";
import { verbalTheme } from "./themes/verbal.theme";
import { BaseLayoutProvider } from "./BaseLayout.context";
import { Header } from "../../core/components/header";
import { useAuth } from "../../contexts/auth.context";
import { useAppView } from "../../contexts/appView.context";
import { useLocation } from "react-router-dom";
import { useOrganisation } from "../../hooks/useOrganisation";
import WarningIcon from "@mui/icons-material/Warning";
import { PrimaryButton } from "./Button/Button";
import { MobileNavigator } from "./MobileNavigator";
import { ViewSwitcher } from "./ViewSwitcher";
import clsx from "clsx";
import {
    isDemoOrganisation,
    markLicenceCongratulationsSeen,
    organisationLicenceHasExpired,
    shouldShowLicenceCongratulations,
} from "../../utils/courseDemo";
import { LicenceUnlockedDialog } from "./LicenceUnlockedDialog";

declare module "@mui/styles/defaultTheme" {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}

const BaseLayout: React.FC<{ hasNav?: boolean; children?: React.ReactNode }> = ({
    children,
    hasNav = true,
}): React.ReactElement => {
    const { user } = useAuth();
    const { canSwitch } = useAppView();
    const { pathname } = useLocation();
    const { organisation, getOrganisation } = useOrganisation();
    const [demoDialog, setDemoDialog] = useState<boolean>(false);
    const [congratulationsOpen, setCongratulationsOpen] = useState<boolean>(false);

    useEffect(() => {
        if (user) {
            getOrganisation(user.id);
        }
    }, [user, pathname]);

    useEffect(() => {
        if (!hasNav) {
            setCongratulationsOpen(false);
            return;
        }
        if (shouldShowLicenceCongratulations(organisation, user?.isSchool)) {
            setCongratulationsOpen(true);
        }
    }, [hasNav, organisation, user?.isSchool]);

    const openContactWidget = (subject: string, description: string) => {
        if (window.FreshworksWidget) {
            window.FreshworksWidget("identify", "ticketForm", { name: user?.firstName, email: user?.email });
            window.FreshworksWidget("prefill", "ticketForm", {
                subject,
                description,
                priority: 1,
            });
            window.FreshworksWidget("open");
        }
    };

    const handleDemoContact = () => {
        setDemoDialog(false);
        openContactWidget("Full Account", "I would like to upgrade to a full account");
    };

    const handleExpiredContact = () => {
        openContactWidget("Licence renewal", "My 6-month licence has ended and I would like to renew access.");
    };

    const handleCongratulationsDismiss = () => {
        if (organisation?.id && organisation.licenceEndDate) {
            markLicenceCongratulationsSeen(organisation.id, organisation.licenceEndDate);
        }
        setCongratulationsOpen(false);
    };

    const classes = useBaseLayoutStyle();
    const licenceExpired = Boolean(organisation && organisationLicenceHasExpired(organisation, user?.isVerbal));
    const showDemoBanner = Boolean(
        organisation &&
            isDemoOrganisation(organisation) &&
            !organisationLicenceHasExpired(organisation, user?.isVerbal),
    );
    const toolbarMinHeight = Number(verbalTheme.mixins.toolbar.minHeight) || 48;
    const statusBannerOffset = showDemoBanner || licenceExpired ? toolbarMinHeight : 0;
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={verbalTheme}>
                <CssBaseline />
                <BaseLayoutProvider>
                    <Dialog open={demoDialog} onClose={() => setDemoDialog(false)}>
                        <DialogTitle>
                            <Typography variant="h4">Course Demo</Typography>
                        </DialogTitle>
                        <DialogContent>
                            <Typography variant="h5">What is Course Demo?</Typography>
                            <Typography paragraph>
                                Course demo is your training account. You get your own organisation with the story
                                collection assigned for your cohort so you can create a programme, add participants, and
                                practise Prepare and Deliver.
                            </Typography>
                            <Typography variant="h5">Why Course Demo?</Typography>
                            <Typography paragraph>
                                Hands-on practice during the course sits alongside trainer screen-share. You only have
                                access to your assigned collection until you complete that first programme.
                            </Typography>
                            <Typography variant="h5">What Next?</Typography>
                            <Typography paragraph>
                                When every session of your first programme is complete, your account upgrades
                                automatically to a full 6-month licence with all published collections. If you need
                                help, contact us:{" "}
                                <Link variant="h6" component="button" onClick={handleDemoContact} underline="hover">
                                    Contact Verbal
                                </Link>
                                .
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <PrimaryButton onClick={() => setDemoDialog(false)}>OK</PrimaryButton>
                        </DialogActions>
                    </Dialog>
                    {hasNav && organisation?.licenceEndDate && (
                        <LicenceUnlockedDialog
                            open={congratulationsOpen}
                            licenceEndDate={organisation.licenceEndDate}
                            onDismiss={handleCongratulationsDismiss}
                        />
                    )}
                    {showDemoBanner && (
                        <>
                            <AppBar position="fixed" elevation={0} className={classes.demoBar}>
                                <Toolbar style={{ justifyContent: "space-evenly" }}>
                                    <WarningIcon style={{ fill: "white", paddingRight: ".5rem" }} fontSize="large" />
                                    <Typography style={{ color: "white" }} color="primary" align="center">
                                        Course demo — complete your first programme on your assigned collection to
                                        unlock a full 6-month licence. Click learn more for details.
                                    </Typography>
                                    <Button
                                        style={{ color: "white" }}
                                        disableRipple
                                        variant="text"
                                        onClick={() => setDemoDialog(true)}
                                    >
                                        Learn More
                                    </Button>
                                </Toolbar>
                            </AppBar>
                            <Toolbar />
                        </>
                    )}
                    {licenceExpired && (
                        <>
                            <AppBar position="fixed" elevation={0} className={classes.expiredBar}>
                                <Toolbar style={{ justifyContent: "space-evenly" }}>
                                    <WarningIcon style={{ fill: "white", paddingRight: ".5rem" }} fontSize="large" />
                                    <Typography style={{ color: "white" }} color="primary" align="center">
                                        Your 6-month licence has ended. Contact Verbal to renew access.
                                    </Typography>
                                    <Button
                                        style={{ color: "white" }}
                                        disableRipple
                                        variant="text"
                                        onClick={handleExpiredContact}
                                    >
                                        Contact Verbal
                                    </Button>
                                </Toolbar>
                            </AppBar>
                            <Toolbar />
                        </>
                    )}
                    {canSwitch && (
                        <Hidden smDown implementation="css">
                            <AppBar
                                position="fixed"
                                elevation={0}
                                className={classes.viewBar}
                                style={{ top: statusBannerOffset }}
                            >
                                <Toolbar variant="dense" style={{ justifyContent: "flex-end", minHeight: 48 }}>
                                    <Typography variant="body2" style={{ color: "white", marginRight: "1rem" }}>
                                        View
                                    </Typography>
                                    <ViewSwitcher />
                                </Toolbar>
                            </AppBar>
                            <Toolbar variant="dense" />
                        </Hidden>
                    )}
                    {hasNav === false && <Header />}
                    <div className={classes.root}>
                        {hasNav && (
                            <>
                                <nav className={clsx(classes.drawer, "navigatorContainer")}>
                                    <Hidden smDown implementation="css">
                                        <Navigator />
                                    </Hidden>
                                </nav>
                            </>
                        )}
                        <main className={clsx(classes.main, "mainContainer")}>{children}</main>
                    </div>
                    {hasNav && <MobileNavigator />}
                </BaseLayoutProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export const LayoutWithNav: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <BaseLayout>{children}</BaseLayout>
);
export const LayoutWithoutNav: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <BaseLayout hasNav={false}>{children}</BaseLayout>
);
